import { useEffect, useState } from "react";
import { Collapse } from "react-collapse";

import useMeetingStore from "@store/useMeetingStore";
import { useOptimisticTags } from "./hooks/useOptimisticTags";
import { ITagResponse } from "@interfaces/responses.interface";
import { cn } from "@utils/tailwind.util";
import { CollapsibleSectionHeader } from "@components/Headers/CollapsibleSectionHeader";
import { TaggingComponentWithTags } from "../../TaggingComponent/TaggingComponentWithTags";
import { RelationObjectType } from "@enums/relations.enum";

interface Props {
  topics: ITagResponse[];
  contentType: RelationObjectType;
  className?: string;
}

export function MeetingTopicsSection({ topics, contentType, className }: Props) {
  const { meetingId } = useMeetingStore();

  const [isSectionOpen, setIsSectionOpen] = useState(true);
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);

  const handleToggleSection = () => setIsSectionOpen((prevState) => !prevState);

  const handleOpenDropdown = () => {
    if (!isSectionOpen) setIsSectionOpen(true);
    setIsDropdownOpened(true);
  };

  const handleCloseDropdown = () => setIsDropdownOpened(false);

  const { currentTags, updateTags, onRemoveTag, setCurrentTags, temporaryTags } = useOptimisticTags(
    contentType,
    meetingId,
    topics,
    "discussed_topic"
  );

  useEffect(() => {
    topics && setCurrentTags(topics);
  }, [setCurrentTags, topics]);

  const tagsCount = currentTags?.length || 0;
  const excludedIds = currentTags.map((item) => item.object.id);

  return (
    <section className={cn("flex flex-col gap-3", className)}>
      <CollapsibleSectionHeader
        title="Discussed Topics"
        tagsCount={tagsCount}
        isOpen={isSectionOpen}
        onToggleSection={handleToggleSection}
        onOpenDropdown={handleOpenDropdown}
      />

      <Collapse isOpened={isSectionOpen}>
        <TaggingComponentWithTags
          contentType={contentType}
          keyForTypes={"MEETING_TOPICS"}
          onRemoveTag={onRemoveTag}
          onChangeTags={updateTags}
          selectedTags={currentTags}
          temporaryTags={temporaryTags}
          excludedIds={excludedIds}
          isDropdownOpened={isDropdownOpened}
          onCloseDropdown={handleCloseDropdown}
          dropdownClassNames="min-w-[280px]"
        />
      </Collapse>
    </section>
  );
}
